import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { API, useAuth } from '../contexts/AuthContext';

const COR = '#5C3D52'; // roxo do marketing

const ABAS = [
  { k: 'dashboard', label: 'Painel', icon: 'bi-speedometer2' },
  { k: 'jogos', label: 'Jogos', icon: 'bi-calendar-event' },
  { k: 'participantes', label: 'Participantes', icon: 'bi-people' },
  { k: 'lojas', label: 'Lojas e Prêmios', icon: 'bi-shop' },
  { k: 'divulgacao', label: 'Divulgação e QR', icon: 'bi-qr-code' },
  { k: 'tv', label: 'TV / Telas', icon: 'bi-tv' },
  { k: 'vencedores', label: 'Vencedores', icon: 'bi-trophy' },
];

export default function TorcidaAdmin() {
  const { temPermissao } = useAuth();
  // Gerente de loja (só torcida_loja): vê apenas dados da própria loja, sem gerenciar jogos/prêmios.
  const ehGerente = temPermissao('torcida_loja') && !temPermissao('torcida_ver') && !temPermissao('torcida_gerenciar');
  const abas = ehGerente ? ABAS.filter(a => !['jogos', 'lojas', 'tv'].includes(a.k)) : ABAS;
  const [aba, setAba] = useState('dashboard');
  return (
    <div className="container-fluid py-3">
      <h4 className="fw-bold mb-1">
        <i className="bi bi-flag-fill me-2" style={{ color: COR }}></i>
        Torcida Centrão
      </h4>
      <p className="text-muted mb-3" style={{ fontSize: '0.85rem' }}>
        {ehGerente ? 'Veja os dados, o ranking e o QR Code da sua loja.' : 'Gerencie jogos, resultados, participantes e prêmios da campanha por loja.'}
      </p>

      <ul className="nav nav-tabs nav-tabs-mkt mb-3">
        {abas.map(a => (
          <li className="nav-item" key={a.k}>
            <button className={`nav-link ${aba === a.k ? 'active' : ''}`} onClick={() => setAba(a.k)}>
              <i className={`bi ${a.icon} me-1`}></i>{a.label}
            </button>
          </li>
        ))}
      </ul>

      {aba === 'dashboard' && <AbaDashboard />}
      {aba === 'jogos' && <AbaJogos />}
      {aba === 'participantes' && <AbaParticipantes />}
      {aba === 'lojas' && <AbaLojas />}
      {aba === 'divulgacao' && <AbaDivulgacao />}
      {aba === 'tv' && <AbaTv />}
      {aba === 'vencedores' && <AbaVencedores />}
    </div>
  );
}

function Spinner() {
  return <div className="text-center py-5"><div className="spinner-border" style={{ color: COR }} /></div>;
}

// ============================================================
// PAINEL
// ============================================================
function AbaDashboard() {
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(true);

  useEffect(() => {
    axios.get(`${API}/torcida/dashboard`)
      .then(r => setDados(r.data))
      .catch(() => {})
      .finally(() => setCarregando(false));
  }, []);

  if (carregando) return <Spinner />;
  if (!dados) return <p className="text-muted">Erro ao carregar o painel.</p>;

  const t = dados.totais || {};
  const cards = [
    { lbl: 'Participantes', val: t.participantes, icon: 'bi-people' },
    { lbl: 'Palpites', val: t.palpites, icon: 'bi-card-checklist' },
    { lbl: 'Jogos cadastrados', val: t.jogos, icon: 'bi-calendar-event' },
    { lbl: 'Jogos finalizados', val: t.jogos_finalizados, icon: 'bi-check-circle' },
  ];

  return (
    <>
      <div className="row g-3 mb-4">
        {cards.map(c => (
          <div className="col-6 col-md-3" key={c.lbl}>
            <div className="card card-stat p-3 h-100" style={{ borderLeftColor: COR }}>
              <div style={{ fontSize: '0.72rem', color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{c.lbl}</div>
              <div style={{ fontSize: '2rem', fontWeight: 700, color: COR }}>{c.val ?? 0}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="row g-3">
        <div className="col-md-7">
          <div className="card p-3">
            <h6 className="fw-bold mb-3">Participantes por loja</h6>
            <div className="table-responsive">
              <table className="table table-sm table-mkt mb-0">
                <thead><tr><th>Loja</th><th className="text-center">Clientes</th><th className="text-center">Funcionários</th><th className="text-center">Total</th><th className="text-center">Status</th></tr></thead>
                <tbody>
                  {(dados.por_loja || []).map(l => (
                    <tr key={l.id}>
                      <td>{l.nome_exibicao}</td>
                      <td className="text-center">{l.clientes}</td>
                      <td className="text-center">{l.funcionarios}</td>
                      <td className="text-center fw-bold">{l.total_participantes}</td>
                      <td className="text-center">
                        <span className={`badge ${l.ativo ? 'badge-ativa' : 'badge-encerrada'}`}>{l.ativo ? 'Ativa' : 'Inativa'}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div className="col-md-5">
          <div className="card p-3">
            <h6 className="fw-bold mb-3">Próximos jogos</h6>
            {(dados.proximos_jogos || []).length === 0 && <p className="text-muted small mb-0">Nenhum jogo futuro cadastrado.</p>}
            {(dados.proximos_jogos || []).map(j => (
              <div key={j.id} className="d-flex justify-content-between align-items-center py-2 border-bottom">
                <span>{j.eh_jogo_do_brasil ? '🇧🇷 ' : ''}{j.time_a} x {j.time_b}</span>
                <small className="text-muted">{new Date(j.data_hora).toLocaleString('pt-BR', { timeZone: 'UTC', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</small>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

// ============================================================
// JOGOS — CRUD + resultado
// ============================================================
const JOGO_VAZIO = { time_a: '', time_a_codigo: '', time_b: '', time_b_codigo: '', data_hora: '', eh_jogo_do_brasil: false };

function AbaJogos() {
  const [jogos, setJogos] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [form, setForm] = useState(null);  // jogo em edição/criação
  const [salvando, setSalvando] = useState(false);

  const carregar = useCallback(() => {
    setCarregando(true);
    axios.get(`${API}/torcida/jogos`).then(r => setJogos(r.data)).catch(() => {}).finally(() => setCarregando(false));
  }, []);
  useEffect(() => { carregar(); }, [carregar]);

  const salvar = async () => {
    if (!form.time_a || !form.time_b || !form.data_hora) {
      Swal.fire('Atenção', 'Preencha os dois times e a data/hora.', 'warning'); return;
    }
    setSalvando(true);
    try {
      if (form.id) await axios.put(`${API}/torcida/jogos/${form.id}`, form);
      else await axios.post(`${API}/torcida/jogos`, form);
      setForm(null); carregar();
    } catch (e) {
      Swal.fire('Erro', e.response?.data?.error || 'Não foi possível salvar o jogo.', 'error');
    } finally { setSalvando(false); }
  };

  const lancarResultado = async (jogo) => {
    const { value } = await Swal.fire({
      title: `${jogo.time_a} x ${jogo.time_b}`,
      html: `<div style="display:flex;gap:10px;justify-content:center;align-items:center">
        <input id="pa" type="number" min="0" class="swal2-input" style="width:80px" value="${jogo.placar_a ?? ''}" placeholder="${jogo.time_a}">
        <span>x</span>
        <input id="pb" type="number" min="0" class="swal2-input" style="width:80px" value="${jogo.placar_b ?? ''}" placeholder="${jogo.time_b}"></div>`,
      showCancelButton: true, confirmButtonText: 'Salvar resultado', cancelButtonText: 'Cancelar',
      confirmButtonColor: COR,
      preConfirm: () => {
        const pa = document.getElementById('pa').value, pb = document.getElementById('pb').value;
        if (pa === '' || pb === '') { Swal.showValidationMessage('Informe os dois placares'); return false; }
        return { placar_a: parseInt(pa, 10), placar_b: parseInt(pb, 10) };
      },
    });
    if (!value) return;
    try {
      const r = await axios.post(`${API}/torcida/jogos/${jogo.id}/resultado`, value);
      Swal.fire('Resultado salvo!', `${r.data.atualizados} palpite(s) pontuado(s).`, 'success');
      carregar();
    } catch (e) {
      Swal.fire('Erro', e.response?.data?.error || 'Não foi possível salvar o resultado.', 'error');
    }
  };

  const remover = async (jogo) => {
    const r = await Swal.fire({ title: 'Remover jogo?', text: `${jogo.time_a} x ${jogo.time_b}`, icon: 'warning', showCancelButton: true, confirmButtonText: 'Remover', cancelButtonText: 'Cancelar', confirmButtonColor: '#b53d3d' });
    if (!r.isConfirmed) return;
    try { await axios.delete(`${API}/torcida/jogos/${jogo.id}`); carregar(); }
    catch (e) { Swal.fire('Erro', e.response?.data?.error || 'Não foi possível remover.', 'error'); }
  };

  const importar = async () => {
    const r = await Swal.fire({ title: 'Importar jogos da fase de grupos?', text: 'Traz o calendário do torneio (horários já convertidos pra Brasília). Não duplica jogos já cadastrados.', icon: 'question', showCancelButton: true, confirmButtonText: 'Importar', cancelButtonText: 'Cancelar', confirmButtonColor: COR });
    if (!r.isConfirmed) return;
    try {
      const res = await axios.post(`${API}/torcida/jogos/importar`);
      Swal.fire('Pronto!', `${res.data.criados} jogo(s) importado(s)` + (res.data.pulados ? `, ${res.data.pulados} já existiam.` : '.'), 'success');
      carregar();
    } catch (e) { Swal.fire('Erro', e.response?.data?.error || 'Não foi possível importar.', 'error'); }
  };

  const sincronizar = async () => {
    try {
      const res = await axios.post(`${API}/torcida/jogos/sincronizar-resultados`);
      Swal.fire('Resultados sincronizados', `${res.data.atualizados} jogo(s) atualizado(s) de ${res.data.finalizados_na_api} finalizado(s) na API.`, 'success');
      carregar();
    } catch (e) { Swal.fire('Erro', e.response?.data?.error || 'Não foi possível sincronizar.', 'error'); }
  };

  if (carregando) return <Spinner />;

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <span className="text-muted small">{jogos.length} jogo(s) cadastrado(s)</span>
        <div className="d-flex gap-2 flex-wrap">
          <button className="btn btn-outline-mkt btn-sm" onClick={importar}>
            <i className="bi bi-cloud-download me-1"></i>Importar jogos
          </button>
          <button className="btn btn-outline-mkt btn-sm" onClick={sincronizar} title="Busca resultados na API (football-data.org)">
            <i className="bi bi-arrow-repeat me-1"></i>Sincronizar resultados
          </button>
          <button className="btn btn-primary-mkt btn-sm" onClick={() => setForm({ ...JOGO_VAZIO })}>
            <i className="bi bi-plus-lg me-1"></i>Novo jogo
          </button>
        </div>
      </div>

      <div className="table-responsive">
        <table className="table table-hover table-mkt mb-0">
          <thead><tr><th>Jogo</th><th>Data/hora</th><th className="text-center">Brasil?</th><th className="text-center">Palpites</th><th className="text-center">Resultado</th><th className="text-end">Ações</th></tr></thead>
          <tbody>
            {jogos.map(j => (
              <tr key={j.id}>
                <td>{j.time_a} <span className="text-muted">x</span> {j.time_b}</td>
                <td><small>{new Date(j.data_hora).toLocaleString('pt-BR', { timeZone: 'UTC', day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })}</small></td>
                <td className="text-center">{j.eh_jogo_do_brasil ? '🇧🇷' : '—'}</td>
                <td className="text-center">{j.qtd_palpites}</td>
                <td className="text-center">
                  {j.finalizado
                    ? <span className="fw-bold" style={{ color: COR }}>{j.placar_a} x {j.placar_b}</span>
                    : <span className="text-muted">—</span>}
                </td>
                <td className="text-end">
                  <button className="btn btn-sm btn-link p-0 me-2" title="Lançar/ajustar resultado" onClick={() => lancarResultado(j)}>
                    <i className="bi bi-flag" style={{ color: COR }}></i>
                  </button>
                  <button className="btn btn-sm btn-link p-0 me-2" title="Editar" onClick={() => setForm({ ...j, data_hora: toLocalInput(j.data_hora), eh_jogo_do_brasil: !!j.eh_jogo_do_brasil })}>
                    <i className="bi bi-pencil" style={{ color: '#666' }}></i>
                  </button>
                  <button className="btn btn-sm btn-link p-0" title="Remover" onClick={() => remover(j)}>
                    <i className="bi bi-trash" style={{ color: '#b53d3d' }}></i>
                  </button>
                </td>
              </tr>
            ))}
            {jogos.length === 0 && <tr><td colSpan={6} className="text-center text-muted py-4">Nenhum jogo cadastrado. Clique em "Novo jogo".</td></tr>}
          </tbody>
        </table>
      </div>

      {form && (
        <ModalBox titulo={form.id ? 'Editar jogo' : 'Novo jogo'} onFechar={() => !salvando && setForm(null)}>
          <div className="row g-2">
            <div className="col-8"><label className="form-label small">Time A</label><input className="form-control" value={form.time_a} onChange={e => setForm(f => ({ ...f, time_a: e.target.value }))} placeholder="Ex: Brasil" /></div>
            <div className="col-4"><label className="form-label small">Sigla (3)</label><input className="form-control" maxLength={3} value={form.time_a_codigo} onChange={e => setForm(f => ({ ...f, time_a_codigo: e.target.value.toUpperCase() }))} placeholder="BRA" /></div>
            <div className="col-8"><label className="form-label small">Time B</label><input className="form-control" value={form.time_b} onChange={e => setForm(f => ({ ...f, time_b: e.target.value }))} placeholder="Ex: Sérvia" /></div>
            <div className="col-4"><label className="form-label small">Sigla (3)</label><input className="form-control" maxLength={3} value={form.time_b_codigo} onChange={e => setForm(f => ({ ...f, time_b_codigo: e.target.value.toUpperCase() }))} placeholder="SRB" /></div>
            <div className="col-12"><label className="form-label small">Data e hora do jogo</label><input type="datetime-local" className="form-control" value={form.data_hora} onChange={e => setForm(f => ({ ...f, data_hora: e.target.value }))} /></div>
            <div className="col-12">
              <div className="form-check mt-2">
                <input className="form-check-input" type="checkbox" id="ehbr" checked={form.eh_jogo_do_brasil} onChange={e => setForm(f => ({ ...f, eh_jogo_do_brasil: e.target.checked }))} />
                <label className="form-check-label" htmlFor="ehbr">É jogo do Brasil (palpite obrigatório)</label>
              </div>
            </div>
          </div>
          <div className="d-flex justify-content-end gap-2 mt-3">
            <button className="btn btn-outline-mkt btn-sm" onClick={() => setForm(null)} disabled={salvando}>Cancelar</button>
            <button className="btn btn-primary-mkt btn-sm" onClick={salvar} disabled={salvando}>{salvando ? 'Salvando...' : 'Salvar'}</button>
          </div>
        </ModalBox>
      )}
    </>
  );
}

// ============================================================
// PARTICIPANTES
// ============================================================
function AbaParticipantes() {
  const { temPermissao } = useAuth();
  const podeGerenciar = temPermissao('torcida_gerenciar');
  const [lista, setLista] = useState([]);
  const [lojas, setLojas] = useState([]);
  const [filtroLoja, setFiltroLoja] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('');
  const [busca, setBusca] = useState('');
  const [carregando, setCarregando] = useState(true);
  const [verPalpites, setVerPalpites] = useState(null); // { participante, palpites } | null
  const [carregandoPalpites, setCarregandoPalpites] = useState(false);

  useEffect(() => { axios.get(`${API}/torcida/lojas`).then(r => setLojas(r.data)).catch(() => {}); }, []);

  const abrirPalpites = (p) => {
    setCarregandoPalpites(true);
    setVerPalpites({ participante: { nome: p.nome, loja: p.loja, eh_funcionario: p.eh_funcionario }, palpites: null });
    axios.get(`${API}/torcida/participantes/${p.id}/palpites`)
      .then(r => setVerPalpites(r.data))
      .catch(e => { Swal.fire('Erro', e.response?.data?.error || 'Não foi possível carregar os palpites.', 'error'); setVerPalpites(null); })
      .finally(() => setCarregandoPalpites(false));
  };

  const carregar = useCallback(() => {
    setCarregando(true);
    const params = new URLSearchParams();
    if (filtroLoja) params.set('loja_id', filtroLoja);
    if (filtroTipo) params.set('tipo', filtroTipo);
    if (busca) params.set('busca', busca);
    axios.get(`${API}/torcida/participantes?${params}`).then(r => setLista(r.data)).catch(() => {}).finally(() => setCarregando(false));
  }, [filtroLoja, filtroTipo, busca]);
  useEffect(() => { const t = setTimeout(carregar, 300); return () => clearTimeout(t); }, [carregar]);

  const excluir = (p) => {
    Swal.fire({
      title: `Excluir ${p.nome}?`,
      html: `Apaga o cadastro e <b>${p.palpites} palpite(s)</b>. Ação <b>irreversível</b>.`,
      icon: 'warning', showCancelButton: true,
      confirmButtonText: 'Excluir', cancelButtonText: 'Cancelar', confirmButtonColor: '#c0392b',
    }).then((res) => {
      if (!res.isConfirmed) return;
      axios.delete(`${API}/torcida/participantes/${p.id}`)
        .then((r) => { Swal.fire({ icon: 'success', title: 'Excluído', text: `Removido(s) ${r.data.palpites_removidos} palpite(s).`, timer: 1600, showConfirmButton: false }); carregar(); })
        .catch((e) => Swal.fire({ icon: 'error', title: 'Erro', text: e.response?.data?.error || 'Não foi possível excluir.' }));
    });
  };

  const editar = (p) => {
    Swal.fire({
      title: 'Editar nome', input: 'text', inputValue: p.nome, inputLabel: 'Nome de exibição',
      showCancelButton: true, confirmButtonText: 'Salvar', cancelButtonText: 'Cancelar', confirmButtonColor: COR,
      inputValidator: (v) => (!v || v.trim().length < 2 ? 'Informe um nome válido' : undefined),
    }).then((res) => {
      if (!res.isConfirmed) return;
      axios.patch(`${API}/torcida/participantes/${p.id}`, { nome: res.value.trim() })
        .then(() => { Swal.fire({ icon: 'success', title: 'Salvo', timer: 1200, showConfirmButton: false }); carregar(); })
        .catch((e) => Swal.fire({ icon: 'error', title: 'Erro', text: e.response?.data?.error || 'Não foi possível salvar.' }));
    });
  };

  return (
    <>
      <div className="row g-2 mb-3">
        <div className="col-md-4"><input className="form-control form-control-sm" placeholder="🔎 Buscar por nome..." value={busca} onChange={e => setBusca(e.target.value)} /></div>
        <div className="col-md-4">
          <select className="form-select form-select-sm" value={filtroLoja} onChange={e => setFiltroLoja(e.target.value)}>
            <option value="">Todas as lojas</option>
            {lojas.map(l => <option key={l.id} value={l.id}>{l.nome_exibicao}</option>)}
          </select>
        </div>
        <div className="col-md-4">
          <select className="form-select form-select-sm" value={filtroTipo} onChange={e => setFiltroTipo(e.target.value)}>
            <option value="">Clientes e funcionários</option>
            <option value="cliente">Só clientes</option>
            <option value="funcionario">Só funcionários</option>
          </select>
        </div>
      </div>

      {carregando ? <Spinner /> : (
        <div className="table-responsive">
          <table className="table table-hover table-mkt mb-0">
            <thead><tr><th>Nome</th><th>Loja</th><th className="text-center">Tipo</th><th className="text-center">Palpites</th><th className="text-center">Pontos</th>{podeGerenciar && <th className="text-center">Ações</th>}</tr></thead>
            <tbody>
              {lista.map(p => (
                <tr key={p.id}>
                  <td>{p.nome}</td>
                  <td>{p.loja}</td>
                  <td className="text-center"><span className={`badge ${p.eh_funcionario ? 'badge-filial' : 'badge-ativa'}`}>{p.eh_funcionario ? 'Funcionário' : 'Cliente'}</span></td>
                  <td className="text-center">
                    {p.palpites > 0
                      ? <button className="btn btn-sm btn-link p-0 fw-bold text-decoration-none" style={{ color: COR }} title="Ver palpites e pontuação" onClick={() => abrirPalpites(p)}>{p.palpites} <i className="bi bi-eye"></i></button>
                      : <span className="text-muted">0</span>}
                  </td>
                  <td className="text-center fw-bold" style={{ color: COR }}>{p.pontos}</td>
                  {podeGerenciar && (
                    <td className="text-center text-nowrap">
                      <button className="btn btn-sm btn-outline-secondary me-1" title="Editar nome" onClick={() => editar(p)}><i className="bi bi-pencil"></i></button>
                      <button className="btn btn-sm btn-outline-danger" title="Excluir" onClick={() => excluir(p)}><i className="bi bi-trash"></i></button>
                    </td>
                  )}
                </tr>
              ))}
              {lista.length === 0 && <tr><td colSpan={podeGerenciar ? 6 : 5} className="text-center text-muted py-4">Nenhum participante encontrado.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {verPalpites && (
        <ModalPalpites dados={verPalpites} carregando={carregandoPalpites} onFechar={() => setVerPalpites(null)} />
      )}
    </>
  );
}

// Modal de homologação: palpites de um participante com placar real e pontos gerados.
const ACERTO_INFO = {
  exato:    { lbl: 'Placar exato', cor: '#1A6B2A', bg: '#e6f4ea' },
  vencedor: { lbl: 'Acertou o vencedor', cor: '#2E3399', bg: '#e8e9f7' },
  empate:   { lbl: 'Acertou o empate', cor: '#9a7d0a', bg: '#fbf4d6' },
  erro:     { lbl: 'Errou', cor: '#6c757d', bg: '#f1f1f1' },
};
function ModalPalpites({ dados, carregando, onFechar }) {
  const { participante, palpites } = dados;
  return (
    <ModalBox titulo="Palpites e pontuação" onFechar={onFechar} largura={680}>
      <div className="d-flex justify-content-between align-items-start mb-3 flex-wrap gap-2">
        <div>
          <div className="fw-bold" style={{ fontSize: '1.05rem' }}>{participante.nome}</div>
          <div className="small text-muted">
            {participante.loja} · <span className={`badge ${participante.eh_funcionario ? 'badge-filial' : 'badge-ativa'}`}>{participante.eh_funcionario ? 'Funcionário' : 'Cliente'}</span>
          </div>
        </div>
        {palpites && (
          <div className="text-end">
            <div className="small text-muted">Total de pontos</div>
            <div className="fw-bold" style={{ color: COR, fontSize: '1.4rem' }}>{participante.total_pontos}</div>
          </div>
        )}
      </div>

      {carregando || !palpites ? <Spinner /> : palpites.length === 0 ? (
        <p className="text-muted text-center py-3 mb-0">Este participante ainda não fez nenhum palpite.</p>
      ) : (
        <>
          <div className="table-responsive">
            <table className="table table-sm align-middle mb-2">
              <thead>
                <tr className="text-muted small">
                  <th>Jogo</th>
                  <th className="text-center">Palpite</th>
                  <th className="text-center">Resultado</th>
                  <th className="text-center">Pontos</th>
                </tr>
              </thead>
              <tbody>
                {palpites.map(p => {
                  const info = p.acerto ? ACERTO_INFO[p.acerto] : null;
                  return (
                    <tr key={p.jogo_id}>
                      <td>
                        <div className="fw-semibold">
                          {p.eh_jogo_do_brasil && '🇧🇷 '}{p.time_a} <span className="text-muted">x</span> {p.time_b}
                        </div>
                        <div className="small text-muted">
                          {new Date(p.data_hora).toLocaleString('pt-BR', { timeZone: 'UTC', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                          {' · '}{p.tipo === 'obrigatorio_brasil' ? 'Brasil' : 'Livre'}
                        </div>
                      </td>
                      <td className="text-center fw-bold text-nowrap">{p.palpite.a} <span className="text-muted">x</span> {p.palpite.b}</td>
                      <td className="text-center text-nowrap">
                        {p.finalizado
                          ? <span className="fw-bold">{p.resultado.a} <span className="text-muted">x</span> {p.resultado.b}</span>
                          : <span className="badge bg-light text-muted border">aguardando</span>}
                      </td>
                      <td className="text-center">
                        {p.finalizado && info
                          ? <span className="badge" style={{ color: info.cor, background: info.bg }} title={info.lbl}>{p.pontos} pt{p.pontos === 1 ? '' : 's'}</span>
                          : <span className="text-muted small">—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="small text-muted border-top pt-2">
            <strong>Pontuação:</strong> 10 = placar exato · 5 = acertou o vencedor · 3 = acertou o empate · 0 = errou.
            Jogos não finalizados aparecem como <em>aguardando</em> (pontos contam só após o resultado ser lançado).
          </div>
        </>
      )}
    </ModalBox>
  );
}

// ============================================================
// LOJAS E PRÊMIOS
// ============================================================
function AbaLojas() {
  const [lojas, setLojas] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [premios, setPremios] = useState({ clientes: { primeiro: '', segundo: '', terceiro: '' }, funcionarios: { primeiro: '', segundo: '', terceiro: '' } });
  const [salvando, setSalvando] = useState(false);
  const [togglando, setTogglando] = useState(null);

  const carregar = useCallback(() => {
    setCarregando(true);
    Promise.all([
      axios.get(`${API}/torcida/lojas`).then(r => setLojas(r.data)).catch(() => {}),
      axios.get(`${API}/torcida/premios-globais`).then(r => setPremios(r.data)).catch(() => {}),
    ]).finally(() => setCarregando(false));
  }, []);
  useEffect(() => { carregar(); }, [carregar]);

  const setP = (grupo, pos, val) => setPremios(s => ({ ...s, [grupo]: { ...s[grupo], [pos]: val } }));

  const salvarPremios = async () => {
    setSalvando(true);
    try {
      await axios.put(`${API}/torcida/premios-globais`, premios);
      Swal.fire({ icon: 'success', title: 'Prêmios salvos', text: 'Valem pra todas as lojas.', timer: 1600, showConfirmButton: false });
    } catch (e) { Swal.fire('Erro', e.response?.data?.error || 'Não foi possível salvar.', 'error'); }
    finally { setSalvando(false); }
  };

  const toggleAtivo = async (l) => {
    setTogglando(l.id);
    try {
      await axios.put(`${API}/torcida/lojas/${l.id}`, { ativo: !l.ativo });
      carregar();
    } catch (e) { Swal.fire('Erro', e.response?.data?.error || 'Não foi possível alterar.', 'error'); }
    finally { setTogglando(null); }
  };

  if (carregando) return <Spinner />;

  const blocoPremios = (grupo, titulo) => (
    <div className="col-md-6">
      <h6 className="fw-bold mb-2">{titulo}</h6>
      <label className="form-label small mb-1">🥇 1º lugar</label>
      <input className="form-control form-control-sm mb-2" value={premios[grupo].primeiro} onChange={e => setP(grupo, 'primeiro', e.target.value)} placeholder="Ex: Churrasqueira" />
      <label className="form-label small mb-1">🥈 2º lugar</label>
      <input className="form-control form-control-sm mb-2" value={premios[grupo].segundo} onChange={e => setP(grupo, 'segundo', e.target.value)} placeholder="Ex: R$ 500" />
      <label className="form-label small mb-1">🥉 3º lugar</label>
      <input className="form-control form-control-sm mb-2" value={premios[grupo].terceiro} onChange={e => setP(grupo, 'terceiro', e.target.value)} placeholder="Ex: R$ 300" />
    </div>
  );

  return (
    <>
      <div className="card p-3 mb-3">
        <h6 className="fw-bold mb-1">🎁 Prêmios da campanha</h6>
        <p className="text-muted small mb-3">Valem pra <strong>todas as lojas</strong>. No ranking, o cliente vê os prêmios de cliente e o funcionário vê os de funcionário.</p>
        <div className="row g-3">
          {blocoPremios('clientes', '🎁 Clientes')}
          {blocoPremios('funcionarios', '🏅 Funcionários')}
        </div>
        <div className="d-flex justify-content-end mt-2">
          <button className="btn btn-primary-mkt btn-sm" onClick={salvarPremios} disabled={salvando}>{salvando ? 'Salvando...' : 'Salvar prêmios'}</button>
        </div>
      </div>

      <p className="text-muted small mb-2">Lojas e QR Codes (o slug vai no link). Use o botão pra ativar/desativar na campanha.</p>
      <div className="row g-3">
        {lojas.map(l => (
          <div className="col-md-6 col-lg-4" key={l.id}>
            <div className="card p-3 h-100 d-flex flex-column">
              <div className="d-flex justify-content-between align-items-start">
                <h6 className="fw-bold mb-0">{l.nome_exibicao}</h6>
                <span className={`badge ${l.ativo ? 'badge-ativa' : 'badge-encerrada'}`}>{l.ativo ? 'Ativa' : 'Inativa'}</span>
              </div>
              <div className="text-muted mb-2" style={{ fontSize: '0.75rem' }}><code>{l.slug}</code> · {l.filiais}</div>
              <button className="btn btn-outline-mkt btn-sm mt-auto" onClick={() => toggleAtivo(l)} disabled={togglando === l.id}>
                {togglando === l.id ? '...' : (l.ativo ? 'Desativar' : 'Ativar')}
              </button>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

// ============================================================
// VENCEDORES
// ============================================================
function AbaVencedores() {
  const [dados, setDados] = useState([]);
  const [carregando, setCarregando] = useState(true);

  useEffect(() => {
    axios.get(`${API}/torcida/vencedores`).then(r => setDados(r.data)).catch(() => {}).finally(() => setCarregando(false));
  }, []);

  if (carregando) return <Spinner />;

  return (
    <>
      <p className="text-muted small mb-3">Top 3 clientes de cada loja (já com critérios de desempate aplicados). Use pra contato e premiação.</p>
      <div className="row g-3">
        {dados.map(loja => (
          <div className="col-md-6" key={loja.slug}>
            <div className="card p-3 h-100">
              <h6 className="fw-bold mb-2">{loja.loja}</h6>
              {loja.vencedores.length === 0 && <p className="text-muted small mb-0">Nenhum participante ainda.</p>}
              {loja.vencedores.map(v => (
                <div key={v.posicao} className="d-flex justify-content-between align-items-center py-1 border-bottom">
                  <div>
                    <strong>{v.posicao}º</strong> {v.nome}
                    <div className="text-muted" style={{ fontSize: '0.72rem' }}>{v.cpf}{v.telefone ? ` · ${v.telefone}` : ''}</div>
                  </div>
                  <span className="fw-bold" style={{ color: COR }}>{v.total_pontos} pts</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

// ============================================================
// DIVULGAÇÃO — regulamento (upload) + links e QR por loja
// ============================================================
function AbaDivulgacao() {
  const { temPermissao } = useAuth();
  const podeGerenciar = temPermissao('torcida_gerenciar'); // gerente de loja (torcida_loja) só lê o regulamento
  const base = window.location.origin;
  const [lojas, setLojas] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [reg, setReg] = useState(null);
  const [subindo, setSubindo] = useState(false);

  const carregarReg = useCallback(() => {
    axios.get(`${API}/torcida/regulamento/status`).then(r => setReg(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    axios.get(`${API}/torcida/lojas`).then(r => setLojas(r.data)).catch(() => {}).finally(() => setCarregando(false));
    carregarReg();
  }, [carregarReg]);

  const subirRegulamento = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.type !== 'application/pdf') { Swal.fire('Atenção', 'Selecione um arquivo PDF.', 'warning'); return; }
    if (file.size > 1.5 * 1024 * 1024) { Swal.fire('Atenção', 'PDF muito grande (máx ~1,5MB). Comprima e tente de novo.', 'warning'); return; }
    const reader = new FileReader();
    reader.onload = async () => {
      setSubindo(true);
      try {
        await axios.post(`${API}/torcida/regulamento`, { pdf_base64: reader.result });
        Swal.fire('Pronto!', 'Regulamento atualizado. O link público já aponta pro novo arquivo.', 'success');
        carregarReg();
      } catch (err) { Swal.fire('Erro', err.response?.data?.error || 'Não foi possível enviar.', 'error'); }
      finally { setSubindo(false); }
    };
    reader.readAsDataURL(file);
  };

  const copiar = (texto) => {
    navigator.clipboard?.writeText(texto);
    Swal.fire({ toast: true, position: 'top', icon: 'success', title: 'Link copiado!', showConfirmButton: false, timer: 1500 });
  };

  const qr = (link) => `https://api.qrserver.com/v1/create-qr-code/?size=220x220&margin=8&data=${encodeURIComponent(link)}`;

  if (carregando) return <Spinner />;

  return (
    <>
      {/* Regulamento */}
      <div className="card p-3 mb-4">
        <h6 className="fw-bold mb-2"><i className="bi bi-file-earmark-pdf me-1" style={{ color: COR }}></i>Regulamento da campanha</h6>
        <p className="text-muted small mb-2">
          O regulamento muda quando os prêmios forem definidos. Suba o PDF atualizado aqui — o link público (no aceite do cliente) passa a apontar pro novo arquivo na hora.
        </p>
        <div className="d-flex flex-wrap align-items-center gap-2">
          {reg?.existe
            ? <a className="btn btn-outline-mkt btn-sm" href={`${API}/publico/torcida/regulamento`} target="_blank" rel="noopener noreferrer"><i className="bi bi-eye me-1"></i>Ver atual</a>
            : <span className="badge badge-rascunho">Nenhum regulamento enviado</span>}
          {podeGerenciar && (
            <label className="btn btn-primary-mkt btn-sm mb-0" style={{ cursor: 'pointer' }}>
              <i className="bi bi-upload me-1"></i>{subindo ? 'Enviando...' : 'Subir novo PDF'}
              <input type="file" accept="application/pdf" hidden onChange={subirRegulamento} disabled={subindo} />
            </label>
          )}
          {reg?.existe && reg.atualizado_em && <small className="text-muted">Atualizado em {new Date(reg.atualizado_em).toLocaleString('pt-BR', { timeZone: 'UTC' })}</small>}
        </div>
      </div>

      {/* QR unico por loja */}
      <p className="text-muted small mb-2">
        <strong>Um QR Code por loja</strong> — clientes e funcionários usam o mesmo. Ao digitar o CPF, o sistema identifica
        automaticamente: se for funcionário ativo, entra no ranking de funcionários; senão, no ranking de clientes.
      </p>
      <div className="row g-3">
        {lojas.map(l => {
          const link = `${base}/torcida/${l.slug}`;
          const linkTv = `${base}/torcida/${l.slug}/tv`;
          const linkTvLeve = `${API}/publico/torcida/tv-lite/${l.slug}`;
          const linkTvEstatica = `${API}/publico/torcida/tv-estatica/${l.slug}`;
          const semJs = !!l.tv_sem_js;
          return (
            <div className="col-md-6 col-lg-4" key={l.id}>
              <div className="card p-3 h-100 text-center">
                <h6 className="fw-bold mb-2">{l.nome_exibicao}</h6>
                <img src={qr(link)} alt={`QR ${l.nome_exibicao}`} width={170} height={170} style={{ margin: '0 auto 8px' }} />
                <div className="text-muted mb-2" style={{ fontSize: '0.72rem', wordBreak: 'break-all' }}>{link}</div>
                <div className="d-flex gap-2 justify-content-center mb-2">
                  <button className="btn btn-outline-mkt btn-sm" onClick={() => copiar(link)}><i className="bi bi-clipboard me-1"></i>Copiar</button>
                  <a className="btn btn-outline-mkt btn-sm" href={qr(link)} target="_blank" rel="noopener noreferrer"><i className="bi bi-download me-1"></i>Baixar QR</a>
                </div>
                <a className="btn btn-primary-mkt btn-sm mb-2" href={linkTv} target="_blank" rel="noopener noreferrer">
                  <i className="bi bi-tv me-1"></i>Abrir TV da loja
                </a>
                {semJs ? (
                  <div className="border-top pt-2 mt-1">
                    <div className="mb-1" style={{ fontSize: '0.72rem' }}>
                      <span className="badge bg-warning text-dark"><i className="bi bi-display me-1"></i>TV antiga</span>{' '}
                      <strong>Use o modo estático</strong> (sem JavaScript) nesta loja:
                    </div>
                    <div className="d-flex gap-2 justify-content-center">
                      <a className="btn btn-primary-mkt btn-sm" href={linkTvEstatica} target="_blank" rel="noopener noreferrer"><i className="bi bi-tv me-1"></i>Abrir TV (estática)</a>
                      <button className="btn btn-outline-mkt btn-sm" onClick={() => copiar(linkTvEstatica)}><i className="bi bi-clipboard me-1"></i>Copiar link</button>
                    </div>
                  </div>
                ) : (
                  <div className="border-top pt-2 mt-1">
                    <div className="text-muted mb-1" style={{ fontSize: '0.72rem' }}>
                      <i className="bi bi-display me-1"></i>TV antiga abrindo em branco? Use o <strong>modo leve</strong>:
                    </div>
                    <div className="d-flex gap-2 justify-content-center">
                      <a className="btn btn-outline-mkt btn-sm" href={linkTvLeve} target="_blank" rel="noopener noreferrer"><i className="bi bi-tv me-1"></i>Abrir TV (modo leve)</a>
                      <button className="btn btn-outline-mkt btn-sm" onClick={() => copiar(linkTvLeve)}><i className="bi bi-clipboard me-1"></i>Copiar link</button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

// ============================================================
// Helpers
// ============================================================
function ModalBox({ titulo, children, onFechar, largura = 480 }) {
  return (
    <div onMouseDown={onFechar} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1050, padding: 16 }}>
      <div onMouseDown={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12, padding: 20, maxWidth: largura, width: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
        <h5 className="fw-bold mb-3">{titulo}</h5>
        {children}
      </div>
    </div>
  );
}

// Converte ISO/datetime do banco pra valor de <input type="datetime-local"> (YYYY-MM-DDTHH:mm) em horario local.
function toLocalInput(dataHora) {
  if (!dataHora) return '';
  const d = new Date(dataHora);
  const p = n => String(n).padStart(2, '0');
  return `${d.getUTCFullYear()}-${p(d.getUTCMonth() + 1)}-${p(d.getUTCDate())}T${p(d.getUTCHours())}:${p(d.getUTCMinutes())}`;
}

// ============================================================
// ABA TV / TELAS — marketing gerencia artes e tempos da TV por loja
// ============================================================
const SLIDE_INFO = {
  ranking: { nome: '🏆 Ranking da loja', desc: 'Obrigatório — gerado pelo sistema. Pode ter uma arte de fundo.' },
  resultado: { nome: '📊 Resultado do último jogo', desc: 'Mostra o placar e quem cravou. Opcional.' },
  convite: { nome: '📲 Convite / QR Code', desc: 'QR pro cliente entrar (gerado pelo sistema). Opcional.' },
  arte: { nome: '🖼️ Arte', desc: 'Imagem que você subiu.' },
};

function AbaTv() {
  const [lojas, setLojas] = useState([]);
  const [slug, setSlug] = useState('');
  const [slides, setSlides] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [subindo, setSubindo] = useState(false);

  useEffect(() => {
    axios.get(`${API}/torcida/lojas`).then(r => {
      setLojas(r.data);
      if (r.data[0]) setSlug(r.data[0].slug);
    }).catch(() => {}).finally(() => setCarregando(false));
  }, []);

  const carregarSlides = useCallback(() => {
    if (!slug) return;
    axios.get(`${API}/torcida/tv-slides/${slug}`).then(r => setSlides(r.data)).catch(() => setSlides([]));
  }, [slug]);
  useEffect(() => { carregarSlides(); }, [carregarSlides]);

  const salvarSlide = async (id, campos) => {
    try { await axios.put(`${API}/torcida/tv-slides/${id}`, campos); carregarSlides(); }
    catch (err) { Swal.fire('Erro', err.response?.data?.error || 'Erro ao salvar', 'error'); }
  };

  // Tipo de ranking da TV desta loja (clientes x funcionários)
  const lojaAtual = lojas.find(l => l.slug === slug);
  const rankingFunc = !!(lojaAtual && lojaAtual.tv_ranking_funcionarios);
  const salvarTipoRanking = async (val) => {
    if (!lojaAtual) return;
    try {
      await axios.put(`${API}/torcida/lojas/${lojaAtual.id}`, { tv_ranking_funcionarios: val });
      const r = await axios.get(`${API}/torcida/lojas`); setLojas(r.data);
      Swal.fire({ icon: 'success', title: val ? 'TV mostrando funcionários' : 'TV mostrando clientes', timer: 1300, showConfirmButton: false });
    } catch (err) { Swal.fire('Erro', err.response?.data?.error || 'Erro ao salvar', 'error'); }
  };

  // TV antiga: servir a página sem JavaScript (rota /tv-estatica). Pra TVs que abrem em branco.
  const semJs = !!(lojaAtual && lojaAtual.tv_sem_js);
  const salvarSemJs = async (val) => {
    if (!lojaAtual) return;
    try {
      await axios.put(`${API}/torcida/lojas/${lojaAtual.id}`, { tv_sem_js: val });
      const r = await axios.get(`${API}/torcida/lojas`); setLojas(r.data);
      Swal.fire({ icon: 'success', title: val ? 'TV antiga: modo sem JavaScript' : 'TV normal (com JavaScript)', timer: 1500, showConfirmButton: false });
    } catch (err) { Swal.fire('Erro', err.response?.data?.error || 'Erro ao salvar', 'error'); }
  };

  const lerImagem = (file, cb) => {
    if (!file) return;
    if (!['image/png', 'image/jpeg'].includes(file.type)) { Swal.fire('Atenção', 'Selecione uma imagem PNG ou JPG.', 'warning'); return; }
    if (file.size > 1.5 * 1024 * 1024) { Swal.fire('Atenção', 'Imagem muito grande (máx ~1,5MB). Comprima e tente de novo.', 'warning'); return; }
    const reader = new FileReader();
    reader.onload = () => cb(reader.result);
    reader.readAsDataURL(file);
  };

  const subirArte = (e) => {
    const file = e.target.files?.[0]; e.target.value = '';
    lerImagem(file, async (b64) => {
      setSubindo(true);
      try { await axios.post(`${API}/torcida/tv-slides/${slug}/arte`, { imagem_base64: b64 }); carregarSlides(); }
      catch (err) { Swal.fire('Erro', err.response?.data?.error || 'Erro ao subir', 'error'); }
      finally { setSubindo(false); }
    });
  };

  const subirFundo = (id, e) => {
    const file = e.target.files?.[0]; e.target.value = '';
    lerImagem(file, async (b64) => {
      setSubindo(true);
      try { await axios.post(`${API}/torcida/tv-slides/${id}/fundo`, { imagem_base64: b64 }); carregarSlides(); }
      catch (err) { Swal.fire('Erro', err.response?.data?.error || 'Erro ao subir', 'error'); }
      finally { setSubindo(false); }
    });
  };

  const removerFundo = async (id) => {
    try { await axios.delete(`${API}/torcida/tv-slides/${id}/fundo`); carregarSlides(); }
    catch (err) { Swal.fire('Erro', err.response?.data?.error || 'Erro', 'error'); }
  };

  const removerArte = async (id) => {
    const r = await Swal.fire({ title: 'Remover esta arte?', icon: 'warning', showCancelButton: true, confirmButtonText: 'Remover', cancelButtonText: 'Cancelar', confirmButtonColor: '#dc3545' });
    if (!r.isConfirmed) return;
    try { await axios.delete(`${API}/torcida/tv-slides/${id}`); carregarSlides(); }
    catch (err) { Swal.fire('Erro', err.response?.data?.error || 'Erro', 'error'); }
  };

  const mover = async (idx, delta) => {
    const alvo = idx + delta;
    if (alvo < 0 || alvo >= slides.length) return;
    const a = slides[idx], b = slides[alvo];
    try {
      await axios.put(`${API}/torcida/tv-slides/${a.id}`, { ordem: b.ordem });
      await axios.put(`${API}/torcida/tv-slides/${b.id}`, { ordem: a.ordem });
      carregarSlides();
    } catch (err) { Swal.fire('Erro', 'Erro ao reordenar', 'error'); }
  };

  if (carregando) return <Spinner />;

  return (
    <>
      <div className="card p-3 mb-3">
        <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
          <h6 className="fw-bold mb-0"><i className="bi bi-tv me-1" style={{ color: COR }}></i>Telas da TV</h6>
          <select className="form-select form-select-sm" style={{ maxWidth: 240 }} value={slug} onChange={e => setSlug(e.target.value)}>
            {lojas.map(l => <option key={l.slug} value={l.slug}>{l.nome_exibicao}</option>)}
          </select>
          <div className="d-flex align-items-center gap-1">
            <small className="text-muted" style={{ whiteSpace: 'nowrap' }} title="Que ranking esta loja mostra na TV. Use 'Funcionários' em lojas sem clientes (ex.: Central).">Ranking da TV:</small>
            <select className="form-select form-select-sm" style={{ maxWidth: 150 }} value={rankingFunc ? 'func' : 'cli'} onChange={e => salvarTipoRanking(e.target.value === 'func')}>
              <option value="cli">Clientes</option>
              <option value="func">Funcionários</option>
            </select>
          </div>
          <div className="form-check form-switch mb-0" title="Algumas TVs muito antigas abrem em branco porque não rodam JavaScript. Ligue para esta loja servir a TV em HTML puro (modo estático, sem JS).">
            <input className="form-check-input" type="checkbox" id="semjs" checked={semJs} onChange={e => salvarSemJs(e.target.checked)} disabled={!lojaAtual} />
            <label className="form-check-label" htmlFor="semjs" style={{ whiteSpace: 'nowrap' }}><i className="bi bi-display me-1"></i>TV antiga (sem JavaScript)</label>
          </div>
          <label className="btn btn-primary-mkt btn-sm mb-0 ms-auto" style={{ cursor: 'pointer' }}>
            <i className="bi bi-upload me-1"></i>{subindo ? 'Enviando...' : 'Subir nova arte'}
            <input type="file" accept="image/png,image/jpeg" hidden onChange={subirArte} disabled={subindo} />
          </label>
        </div>
        {semJs && (
          <div className="alert alert-warning py-2 px-3 mb-2 small">
            <i className="bi bi-info-circle me-1"></i>Esta loja está em <strong>modo estático (sem JavaScript)</strong>. Na aba <strong>Divulgação</strong>, use o link <strong>“Abrir TV (estática)”</strong> na TV desta loja. O rodízio de telas acontece por recarregamento automático da página.
          </div>
        )}
        <p className="text-muted small mb-0">
          A TV passa cada tela pelo tempo definido, na ordem abaixo. O <b>ranking é obrigatório</b>. Suba artes no formato <b>TV (16:9, ex. 1920×1080)</b>, PNG ou JPG até ~1,5MB.
        </p>
      </div>

      {slides.map((s, idx) => {
        const info = SLIDE_INFO[s.tipo] || SLIDE_INFO.arte;
        const ehArte = s.tipo === 'arte';
        const ehRanking = s.tipo === 'ranking';
        return (
          <div key={s.id} className="card p-3 mb-2">
            <div className="d-flex flex-wrap align-items-center gap-3">
              {s.imagem_url
                ? <img src={`${API}${s.imagem_url}`} alt="" style={{ width: 120, height: 68, objectFit: 'cover', borderRadius: 6, border: '1px solid #ddd' }} />
                : <div style={{ width: 120, height: 68, borderRadius: 6, border: '1px dashed #ccc', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.6rem', background: '#fafafa' }}>{info.nome.split(' ')[0]}</div>}
              <div style={{ flex: 1, minWidth: 170 }}>
                <div className="fw-semibold">{info.nome}</div>
                <div className="text-muted" style={{ fontSize: '0.78rem' }}>{info.desc}</div>
              </div>
              <div>
                <label className="d-block text-muted" style={{ fontSize: '0.72rem' }}>Tempo (seg)</label>
                <input type="number" min="3" className="form-control form-control-sm" style={{ width: 90 }}
                  defaultValue={s.duracao_seg}
                  onBlur={e => { const v = parseInt(e.target.value); if (v && v !== s.duracao_seg) salvarSlide(s.id, { duracao_seg: v }); }} />
              </div>
              <div className="form-check form-switch">
                <input className="form-check-input" type="checkbox" checked={!!s.ativo} disabled={ehRanking}
                  onChange={e => salvarSlide(s.id, { ativo: e.target.checked })} />
                <label className="form-check-label" style={{ fontSize: '0.78rem' }}>{ehRanking ? 'Sempre' : 'Mostrar'}</label>
              </div>
              <div className="btn-group-vertical btn-group-sm">
                <button className="btn btn-outline-secondary py-0" disabled={idx === 0} onClick={() => mover(idx, -1)}><i className="bi bi-chevron-up"></i></button>
                <button className="btn btn-outline-secondary py-0" disabled={idx === slides.length - 1} onClick={() => mover(idx, 1)}><i className="bi bi-chevron-down"></i></button>
              </div>
              <div className="d-flex flex-column gap-1">
                {ehRanking && (
                  <>
                    <label className="btn btn-outline-mkt btn-sm mb-0 py-1" style={{ cursor: 'pointer', fontSize: '0.75rem' }}>
                      <i className="bi bi-image me-1"></i>{s.imagem_url ? 'Trocar fundo' : 'Subir fundo'}
                      <input type="file" accept="image/png,image/jpeg" hidden onChange={e => subirFundo(s.id, e)} />
                    </label>
                    {s.imagem_url && (
                      <div style={{ fontSize: '0.68rem' }}>
                        <div className="text-muted mb-1">Onde o ranking entra na arte (% das bordas) — ajuste e veja na TV:</div>
                        <div className="d-flex gap-1 flex-wrap">
                          {[['rk_top', 'Topo'], ['rk_left', 'Esq'], ['rk_right', 'Dir'], ['rk_bottom', 'Baixo']].map(([campo, lbl]) => (
                            <span key={campo} className="d-flex align-items-center gap-1">
                              <small>{lbl}</small>
                              <input type="number" min="0" max="90" className="form-control form-control-sm px-1" style={{ width: 52 }}
                                defaultValue={s[campo] ?? ''}
                                onBlur={e => { const v = e.target.value; if (v !== '' && parseInt(v) !== s[campo]) salvarSlide(s.id, { [campo]: parseInt(v) }); }} />
                            </span>
                          ))}
                        </div>
                        <button className="btn btn-outline-danger btn-sm py-0 mt-1" style={{ fontSize: '0.7rem' }} onClick={() => removerFundo(s.id)}>Remover fundo</button>
                      </div>
                    )}
                  </>
                )}
                {ehArte && <button className="btn btn-outline-danger btn-sm" onClick={() => removerArte(s.id)}><i className="bi bi-trash me-1"></i>Remover</button>}
              </div>
            </div>
          </div>
        );
      })}
      {slides.length === 0 && <div className="text-muted text-center py-4">Nenhuma tela configurada pra esta loja.</div>}
    </>
  );
}
